import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "@/lib/i18n";
import { BRAND_ASSETS } from "@/lib/config";
import { CATEGORIES } from "@/content/categories";
import { LESSONS } from "@/content/lessons";
import { GAMES } from "@/content/games";
import { STORIES } from "@/content/stories";
import Icon from "@/components/shared/Icon";
import Seo from "@/components/shared/Seo";
import FloatingDecor from "@/components/shared/FloatingDecor";
import LessonCard from "@/components/education/LessonCard";

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.08 } }),
};

function Section({ children, className = "" }) {
  return <section className={`mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8 ${className}`}>{children}</section>;
}

function Heading({ title, subtitle, action }) {
  return (
    <div className="mb-10 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
      <div>
        <h2 className="font-display text-2xl font-bold tracking-tight text-[#082B66] sm:text-3xl lg:text-4xl">{title}</h2>
        {subtitle && <p className="mt-2 max-w-xl text-base text-[#475569]">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export default function Home() {
  const { t, lp, code } = useLang();
  const featuredLessons = LESSONS.slice(0, 3);
  const featuredGames = GAMES.slice(0, 3);
  const featuredStories = STORIES.slice(0, 3);

  return (
    <>
      <Seo title={t("brand.tagline")} description={t("home.hero.subtitle")} />

      {/* HERO */}
      <section className="relative isolate overflow-hidden">
        <img
          src={BRAND_ASSETS.background}
          alt="LUMORA magical learning world with floating books, islands, and a treehouse library"
          className="absolute inset-0 h-full w-full object-cover"
          fetchPriority="high"
        />
        <div className="absolute inset-0 lumora-scrim" />
        <FloatingDecor />
        <div className="relative mx-auto flex max-w-7xl flex-col items-start px-4 pb-20 pt-20 sm:px-6 sm:pb-28 sm:pt-28 lg:px-8">
          <motion.div variants={fade} initial="hidden" animate="show" className="max-w-2xl">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/80 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-[#147DEB] shadow-sm ring-1 ring-[#147DEB]/10 backdrop-blur">
              <Icon name="Sparkles" className="h-4 w-4 text-[#FFC928]" />
              Learn • Play • Give
            </span>
            <h1 className="font-display mt-5 text-4xl font-extrabold leading-[1.05] tracking-tight text-[#082B66] drop-shadow-sm sm:text-5xl lg:text-6xl">
              {t("home.hero.title")}
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-[#1E3A5F] sm:text-lg">{t("home.hero.subtitle")}</p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to={lp("/learn")}
                data-testid="hero-start-learning-cta"
                className="inline-flex items-center gap-2 rounded-2xl bg-[#147DEB] px-6 py-3.5 text-base font-bold text-white shadow-lg shadow-[#147DEB]/30 transition-all hover:-translate-y-0.5 hover:bg-[#0F62BD]"
              >
                <Icon name="Rocket" className="h-5 w-5" />
                {t("home.cta.start")}
              </Link>
              <Link
                to={lp("/impact")}
                data-testid="hero-impact-cta"
                className="inline-flex items-center gap-2 rounded-2xl bg-white/90 px-6 py-3.5 text-base font-bold text-[#082B66] shadow-md ring-1 ring-[#147DEB]/15 backdrop-blur transition-all hover:-translate-y-0.5 hover:bg-white"
              >
                <Icon name="HeartHandshake" className="h-5 w-5 text-[#45C878]" />
                {t("home.cta.impact")}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <Section>
        <Heading title={t("home.philosophy.title")} />
        <div className="grid gap-6 md:grid-cols-3">
          {[
            { k: "learn", icon: "GraduationCap", color: "#147DEB" },
            { k: "play", icon: "Gamepad2", color: "#FFC928" },
            { k: "give", icon: "HeartHandshake", color: "#45C878" },
          ].map((p, i) => (
            <motion.div
              key={p.k}
              custom={i}
              variants={fade}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.3 }}
              className="rounded-3xl border-2 border-[#147DEB]/10 bg-white p-8 card-shadow-soft"
            >
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl" style={{ backgroundColor: `${p.color}1f` }}>
                <Icon name={p.icon} className="h-7 w-7" style={{ color: p.color }} />
              </span>
              <h3 className="font-display mt-5 text-2xl font-bold text-[#082B66]">{t(`home.philosophy.${p.k}`)}</h3>
              <p className="mt-2 text-base leading-relaxed text-[#475569]">{t(`home.philosophy.${p.k}Desc`)}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* CATEGORIES */}
      <div className="bg-[#E8F5FF]/50">
        <Section>
          <Heading
            title={t("home.categories.title")}
            subtitle={t("home.categories.subtitle")}
            action={
              <Link to={lp("/learn")} className="inline-flex items-center gap-1.5 text-sm font-bold text-[#147DEB] hover:underline" data-testid="home-view-all-categories">
                {t("common.viewAll")} <Icon name="ArrowRight" className="h-4 w-4" />
              </Link>
            }
          />
          <div className="grid grid-cols-2 gap-4 sm:gap-5 md:grid-cols-3 lg:grid-cols-4">
            {CATEGORIES.map((c, i) => (
              <motion.div key={c.id} custom={i} variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }}>
                <Link
                  to={lp(`/learn?cat=${c.id}`)}
                  data-testid={`home-category-${c.id}`}
                  className="group flex h-full flex-col rounded-3xl border-2 border-white bg-white p-5 transition-all hover:-translate-y-1 hover:shadow-lg"
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl" style={{ backgroundColor: `${c.color}1f` }}>
                    <Icon name={c.icon} className="h-6 w-6" style={{ color: c.color }} />
                  </span>
                  <h3 className="font-display mt-4 text-lg font-bold text-[#082B66]">{c.name[code]}</h3>
                  <p className="mt-1 text-sm text-[#64748B]">{c.desc[code]}</p>
                </Link>
              </motion.div>
            ))}
          </div>
        </Section>
      </div>

      {/* LESSON PREVIEW + latest resources combined */}
      <Section>
        <Heading
          title={t("home.resources.title")}
          subtitle={t("home.resources.subtitle")}
          action={
            <Link to={lp("/learn")} className="inline-flex items-center gap-1.5 text-sm font-bold text-[#147DEB] hover:underline" data-testid="home-view-all-lessons">
              {t("common.viewAll")} <Icon name="ArrowRight" className="h-4 w-4" />
            </Link>
          }
        />
        <div className="grid gap-6 md:grid-cols-3">
          {featuredLessons.map((l) => (
            <LessonCard key={l.slug} lesson={l} />
          ))}
        </div>
      </Section>

      {/* GAMES */}
      <div className="bg-gradient-to-br from-[#FFF4CC]/60 via-[#FFFCF5] to-[#E8F5FF]/60">
        <Section>
          <Heading
            title={t("home.games.title")}
            subtitle={t("home.games.subtitle")}
            action={
              <Link to={lp("/play")} className="inline-flex items-center gap-1.5 text-sm font-bold text-[#147DEB] hover:underline" data-testid="home-view-all-games">
                {t("common.viewAll")} <Icon name="ArrowRight" className="h-4 w-4" />
              </Link>
            }
          />
          <div className="grid gap-6 md:grid-cols-3">
            {featuredGames.map((g) => (
              <Link
                key={g.id}
                to={lp(`/play?game=${g.id}`)}
                data-testid={`home-game-${g.id}`}
                className="group rounded-3xl border-4 bg-white p-6 transition-all hover:-translate-y-1"
                style={{ borderColor: `${g.color}80` }}
              >
                <span className="flex h-14 w-14 items-center justify-center rounded-2xl" style={{ backgroundColor: `${g.color}1f` }}>
                  <Icon name={g.icon} className="h-7 w-7" style={{ color: g.color }} />
                </span>
                <h3 className="font-display mt-4 text-xl font-bold text-[#082B66]">{g.title[code]}</h3>
                <p className="mt-2 text-sm text-[#475569]">{g.desc[code]}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-[#147DEB]">
                  {t("common.playNow")} <Icon name="Play" className="h-4 w-4" />
                </span>
              </Link>
            ))}
          </div>
        </Section>
      </div>

      {/* STORIES */}
      <Section>
        <Heading
          title={t("home.stories.title")}
          subtitle={t("home.stories.subtitle")}
          action={
            <Link to={lp("/stories")} className="inline-flex items-center gap-1.5 text-sm font-bold text-[#147DEB] hover:underline" data-testid="home-view-all-stories">
              {t("common.viewAll")} <Icon name="ArrowRight" className="h-4 w-4" />
            </Link>
          }
        />
        <div className="grid gap-6 md:grid-cols-3">
          {featuredStories.map((s) => (
            <Link
              key={s.slug}
              to={lp(`/stories/${s.slug}`)}
              data-testid={`home-story-${s.slug}`}
              className="group overflow-hidden rounded-3xl border-2 border-[#147DEB]/10 bg-white transition-all hover:-translate-y-1 hover:shadow-xl"
            >
              <div className="flex h-36 items-center justify-center" style={{ backgroundColor: s.cover }}>
                <Icon name="BookOpen" className="h-14 w-14 text-white/90" />
              </div>
              <div className="p-6">
                <h3 className="font-display text-xl font-bold text-[#082B66]">{s.title[code]}</h3>
                <p className="mt-2 text-sm text-[#475569]">{s.desc[code]}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-[#147DEB]">
                  {t("common.readStory")} <Icon name="ArrowRight" className="h-4 w-4" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </Section>

      {/* BANNER + community + impact + giggle */}
      <div className="bg-[#082B66]">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8">
          <div className="overflow-hidden rounded-3xl ring-1 ring-white/10">
            <img src={BRAND_ASSETS.banner} alt="LUMORA mascots celebrating learning" className="w-full object-cover" loading="lazy" />
          </div>

          <div className="mt-14 grid gap-6 md:grid-cols-2">
            <div className="rounded-3xl bg-white/5 p-8 ring-1 ring-white/10">
              <Icon name="Users" className="h-8 w-8 text-[#FFC928]" />
              <h3 className="font-display mt-4 text-2xl font-bold text-white">{t("home.community.title")}</h3>
              <p className="mt-2 text-base leading-relaxed text-white/75">{t("home.community.desc")}</p>
              <Link to={lp("/community")} className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-[#FFC928] hover:underline" data-testid="home-community-link">
                {t("common.learnMore")} <Icon name="ArrowRight" className="h-4 w-4" />
              </Link>
            </div>
            <div className="rounded-3xl bg-white/5 p-8 ring-1 ring-white/10">
              <Icon name="ShieldCheck" className="h-8 w-8 text-[#45C878]" />
              <h3 className="font-display mt-4 text-2xl font-bold text-white">{t("home.impact.title")}</h3>
              <p className="mt-2 text-base leading-relaxed text-white/75">{t("home.impact.desc")}</p>
              <Link to={lp("/impact")} className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-[#45C878] hover:underline" data-testid="home-impact-link">
                {t("common.learnMore")} <Icon name="ArrowRight" className="h-4 w-4" />
              </Link>
            </div>
          </div>

          <div className="mt-6 rounded-3xl bg-gradient-to-br from-[#147DEB]/20 to-[#45C878]/10 p-8 ring-1 ring-white/10">
            <span className="inline-flex items-center gap-2 rounded-full bg-[#FFC928] px-3 py-1 text-xs font-bold text-[#082B66]">
              <Icon name="Heart" className="h-4 w-4" /> Giggle Academy
            </span>
            <h3 className="font-display mt-4 text-2xl font-bold text-white">{t("home.giggle.title")}</h3>
            <p className="mt-2 max-w-3xl text-base leading-relaxed text-white/80">{t("home.giggle.desc")}</p>
            <Link to={lp("/about")} className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-[#FFC928] hover:underline" data-testid="home-giggle-link">
              {t("common.learnMore")} <Icon name="ArrowRight" className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
